import type { Permission } from '@/domain/permissions';
import { useAccessToken } from '@/hooks/authority/useAccessToken';
import { useIsLoggedIn } from '@/hooks/authority/useIsLoggedIn';
import { useMe } from '@/hooks/services/user';
import { usePermissions } from '@/hooks/usePermissions';
import ErrorBoundary from '@/pages/ErrorBoundary';
import type { PropifyRoute } from '@/routes';
import { routes, unauthorizedRoute } from '@/routes';
import { SentryService } from '@/services/sentry';
import { getPermissionsFromUser, hasPermissions, logout, setPermissions } from '@/utils/authority';
import { PageLoading } from '@ant-design/pro-layout';
import { Suspense, useEffect, useMemo, useState, type FC } from 'react';
import { Navigate, Outlet, RouterProvider, type RouteObject } from 'react-router';

const MAXIMUM_ME_RETRIES = 1;

const filterRoutes = (
  routesList: readonly PropifyRoute[],
  permissions: Permission[],
): PropifyRoute[] =>
  routesList
    .map((route) =>
      !route.permissions || hasPermissions(route.permissions, permissions)
        ? route
        : { ...unauthorizedRoute, path: route.path, children: route.children },
    )
    .map((route) => ({
      ...route,
      children: route.children?.length ? filterRoutes(route.children, permissions) : undefined,
    }));

const generateRoutes: (routesList: readonly PropifyRoute[]) => RouteObject[] = (routesList) =>
  routesList.map((route) => ({
    path: route.path,
    element: route.redirect ? (
      <Navigate to={route.redirect} />
    ) : route.component ? (
      <Suspense fallback={<PageLoading />}>
        {/* @ts-ignore */}
        <route.component route={route} {...route.props} />
      </Suspense>
    ) : (
      <Outlet />
    ),
    children: !route.redirect && route.children ? generateRoutes(route.children) : undefined,
    ErrorBoundary,
  }));

export const Router: FC = () => {
  const { isLoggedIn } = useIsLoggedIn();
  const { accessToken } = useAccessToken();
  const { permissions } = usePermissions();
  const [, setMeRetryCount] = useState(0);

  const {
    data: currentUser,
    error,
    refetch: refetchUser,
  } = useMe({
    disabled: !isLoggedIn,
  });

  useEffect(() => {
    if (!error) {
      return;
    }
    setMeRetryCount((meRetryCount) => {
      if (meRetryCount >= MAXIMUM_ME_RETRIES) {
        logout();
        return 0;
      }
      return meRetryCount + 1;
    });
  }, [error]);

  useEffect(() => {
    if (!currentUser) {
      return;
    }
    setPermissions(getPermissionsFromUser(currentUser));
  }, [currentUser, accessToken]);

  useEffect(() => {
    if (!accessToken || !currentUser) {
      return;
    }
    refetchUser();
  }, [accessToken]);

  const permissionsRouter = useMemo(
    () =>
      SentryService.sentryCreateBrowserRouter([
        {
          path: '/',
          element: <Outlet />,
          children: generateRoutes(filterRoutes(routes, isLoggedIn ? permissions : [])),
          ErrorBoundary,
        },
      ]),
    [isLoggedIn, permissions],
  );

  const key = currentUser ? currentUser.id + permissions.join(',') : 'loggedOut';

  return <RouterProvider key={key} router={permissionsRouter} />;
};
